<template>
  <div class="public-view-wrapper">
    <v-row align="center" justify="center" no-gutters class="fill-height">
      <v-col cols="11" sm="10" md="8" lg="5" xl="4">
        <v-slide-y-transition appear>
          <v-card v-if="requestData" elevation="12" class="mx-auto overflow-hidden main-content-card">
            <!-- Top spacing banner space -->
            <div class="top-spacer"></div>

            <div class="card-content text-center">
              <!-- Logo -->
              <div class="logo-container">
                <v-img
                  v-if="requestData.housing_unit_logo"
                  :src="requestData.housing_unit_logo"
                  class="mx-auto house-logo"
                  contain
                  alt="Housing Unit Logo"
                ></v-img>
              </div>

              <!-- Housing Unit Badge -->
              <div class="mb-6 mb-sm-8">
                <v-chip
                  color="#1e2d3e"
                  dark
                  label
                  class="px-4 px-sm-6 py-2 subtitle-2 font-weight-bold elevation-1 housing-badge"
                >
                  {{ requestData.housing_unit_name }}
                </v-chip>
              </div>

              <!-- Title -->
              <h1 class="title-primary mb-4 mb-sm-6">
                {{ $t('solicitud_union_domicilio') }}
              </h1>

              <!-- Greeting -->
              <div class="greeting-text mb-6 mb-sm-8">
                {{ $t('hola') }} <span class="font-weight-bold">{{ requestData.idUser.first_name }} {{ requestData.idUser.last_name }}</span>,
              </div>

              <!-- Description -->
              <div class="description-text mb-10 mb-sm-12">
                <p>
                  {{ $t('el_usuario') }} <span class="font-weight-bold">{{ requestData.idUserResident.first_name }} {{ requestData.idUserResident.last_name }}</span>
                  {{ $t('ha_enviado_solicitud') }}
                  <span class="font-weight-bold">{{ $t('residente_secundario') }}</span>
                  {{ $t('de_su_domicilio_en_el') }} {{ requestData.housing_unit_name }}.
                </p>
              </div>

              <!-- Action Button -->
              <div class="action-container mb-8 mb-sm-12">
                <v-btn
                  color="#00AD5C"
                  x-large
                  block
                  dark
                  class="accept-btn elevation-4"
                  :loading="loadingAccept"
                  @click="acceptRequest"
                >
                  {{ $t('aceptar_solicitud') }}
                </v-btn>
              </div>

              <!-- Footer Note -->
              <p class="ignore-note mb-4 mb-sm-6">
                {{ $t('ignorar_solicitud') }}
              </p>
            </div>

            <!-- Bottom Footer Banner -->
            <div class="footer-banner py-4 py-sm-5 text-center">
              <span class="caption text-grey-darken-2">Intrare Residencia.</span>
            </div>
          </v-card>
        </v-slide-y-transition>

        <!-- Skeleton Loader -->
        <v-card v-if="loading" elevation="4" class="mx-auto pa-8 pa-sm-12 main-content-card text-center">
          <v-skeleton-loader type="image, chip, heading, text, button, text" class="mx-auto"></v-skeleton-loader>
        </v-card>

        <!-- Error Message -->
        <v-fade-transition>
          <v-alert
            v-if="!loading && errorMsg"
            type="error"
            prominent
            border="left"
            elevation="4"
            class="mx-auto"
          >
            <v-row align="center">
              <v-col class="grow">{{ errorMsg }}</v-col>
              <v-col class="shrink">
                <v-btn text @click="fetchData">{{ $t('reintentar') }}</v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </v-fade-transition>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'RequestJoinHouse',
  data () {
    return {
      pk: this.$route.params.pk,
      loading: true,
      loadingAccept: false,
      requestData: null,
      errorMsg: ''
    }
  },
  created () {
    this.fetchData()
  },
  methods: {
    fetchData () {
      this.loading = true
      this.errorMsg = ''
      const url = `https://residencial-services.intrare.app/request_join_house/public/${this.pk}/`
      axios.get(url)
        .then(response => {
          this.requestData = response.data
          this.loading = false
        })
        .catch(error => {
          console.error('Error fetching request data:', error)
          this.errorMsg = this.$t('solicitud_no_encontrada_o_expirada')
          this.loading = false
        })
    },
    acceptRequest () {
      this.loadingAccept = true
      const url = `https://residencial-services.intrare.app/request_join_house/accept/${this.pk}/`
      axios.get(url)
        .then(() => {
          this.loadingAccept = false
          this.$router.push('/user_confirmed_successfully')
        })
        .catch(error => {
          this.loadingAccept = false
          console.error('Error accepting request:', error)
          let message = this.$t('error_aceptar_solicitud')
          if (error.response && error.response.data && (error.response.data.detail || error.response.data.error)) {
            message = error.response.data.detail || error.response.data.error
          }
          this.$store.commit('snackbar_activate_true')
          this.$store.commit('snackbar_change_color', 'error')
          this.$store.commit('snackbar_change_message', message)
        })
    }
  },
  i18n: {
    messages: {
      es: {
        solicitud_union_domicilio: 'Solicitud de Unión a Domicilio',
        hola: 'Hola',
        el_usuario: 'El usuario',
        ha_enviado_solicitud: 'te ha enviado una solicitud para unirte como',
        residente_secundario: 'Residente Secundario',
        de_su_domicilio_en_el: 'de su domicilio en el fraccionamiento',
        aceptar_solicitud: 'Aceptar Solicitud',
        ignorar_solicitud: 'Si no esperabas esta solicitud, por favor ignora este mensaje.',
        cargando_informacion: 'Cargando información...',
        error_cargar_solicitud: 'Ocurrió un error al cargar la solicitud.',
        solicitud_no_encontrada_o_expirada: 'La solicitud no fue encontrada o ya ha expirado.',
        solicitud_aceptada_exito: 'Solicitud aceptada con éxito.',
        error_aceptar_solicitud: 'Ocurrió un error al intentar aceptar la solicitud.',
        reintentar: 'Reintentar'
      },
      en: {
        solicitud_union_domicilio: 'Join House Request',
        hola: 'Hello',
        el_usuario: 'The user',
        ha_enviado_solicitud: 'has sent you a request to join as a',
        residente_secundario: 'Secondary Resident',
        de_su_domicilio_en_el: 'at their home in the housing unit',
        aceptar_solicitud: 'Accept Request',
        ignorar_solicitud: 'If you were not expecting this request, please ignore this message.',
        cargando_informacion: 'Loading information...',
        error_cargar_solicitud: 'An error occurred while loading the request.',
        solicitud_no_encontrada_o_expirada: 'The request was not found or has already expired.',
        solicitud_aceptada_exito: 'Request accepted successfully.',
        error_aceptar_solicitud: 'An error occurred while trying to accept the request.',
        reintentar: 'Retry'
      }
    }
  }
}
</script>

<style scoped>
.public-view-wrapper {
  width: 100%;
  height: 100%;
  padding-bottom: 24px;
}

.main-content-card {
  border-radius: 20px !important;
  background: #ffffff !important;
  max-width: 100%;
  margin: 0 auto;
}

.top-spacer {
  height: 6px;
  background: linear-gradient(90deg, #1e2d3e 0%, #34495e 100%);
}

.card-content {
  padding: 32px 20px;
}

.logo-container {
  margin-bottom: 32px;
}

.house-logo {
  max-height: 120px;
  max-width: 100%;
}

.housing-badge {
  border-radius: 10px !important;
  font-size: 0.8rem !important;
}

.title-primary {
  color: #1e2d3e;
  font-size: 1.6rem;
  font-weight: 700;
  line-height: 1.25;
}

.greeting-text {
  font-size: 1.15rem;
  color: #4a5568;
}

.description-text {
  font-size: 1rem;
  color: #4a5568;
  line-height: 1.6;
}

.action-container {
  width: 100%;
}

.accept-btn {
  border-radius: 14px !important;
  font-size: 1.15rem !important;
  font-weight: 700 !important;
  text-transform: none !important;
  height: 64px !important;
}

.ignore-note {
  font-size: 0.9rem;
  color: #a0aec0;
  line-height: 1.4;
}

.footer-banner {
  background-color: #f7fafc;
  border-top: 1px solid #edf2f7;
}

/* Tablet and Desktop adjustments */
@media (min-width: 600px) {
  .card-content {
    padding: 60px 48px;
  }
  .title-primary {
    font-size: 2.2rem;
  }
  .greeting-text {
    font-size: 1.35rem;
  }
  .description-text {
    font-size: 1.15rem;
  }
  .house-logo {
    max-height: 150px;
  }
  .housing-badge {
    font-size: 0.85rem !important;
    border-radius: 12px !important;
  }
}

/* Specific small heights or widths */
@media (max-width: 350px) {
  .title-primary {
    font-size: 1.4rem;
  }
  .card-content {
    padding: 24px 16px;
  }
}
</style>
